import { useRef, useState, useEffect, useCallback } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

/**
 * Robust WebSocket hook for STOMP communication
 * @param {Object} options Configuration options
 * @param {string} options.url - Base URL (will automatically append /ws-native or fallback to /ws)
 * @param {string} options.topic - (Optional) Topic to subscribe to immediately
 * @param {Function} options.onMessage - (Optional) Callback for messages on the topic
 * @param {string} options.accessToken - (Optional) JWT Access Token for authentication
 * @param {boolean} options.debug - Enable debug logging
 */
export const useWebSocket = ({ url, topic, onMessage, accessToken, debug = false }) => {
    const clientRef = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState(null);

    // Determine the correct WebSocket URL based on current protocol
    const getWebSocketUrl = useCallback(() => {
        // 1. If explicit URL provided and NOT localhost (unless we are in dev), use it.
        // 2. OR if we are in PRODUCTION, always prefer auto-detecting relative path (ignore .env localhost)
        // 3. Fallback to .env or window.location

        const isProduction = process.env.NODE_ENV === 'production';
        let candidateUrl = url || process.env.REACT_APP_WS_URL;

        // In production, if the candidate URL is localhost, ignore it and force relative
        if (isProduction && candidateUrl && candidateUrl.includes('localhost')) {
            candidateUrl = null;
        }

        let baseUrl = candidateUrl || window.location.origin;

        // Remove trailing slash if present
        baseUrl = baseUrl.replace(/\/$/, '');

        // Check if we already have a full WS/WSS URL
        if (baseUrl.startsWith('ws://') || baseUrl.startsWith('wss://')) {
            return baseUrl;
        }

        // Otherwise, construct from HTTP/HTTPS
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

        // If baseUrl starts with http/https, strip it
        const cleanHost = baseUrl.replace(/^http(s)?:\/\//, '');

        // Default to native endpoint for better performance
        return `${protocol}//${cleanHost}/ws-native`;
    }, [url]);

    const connect = useCallback(() => {
        if (clientRef.current && clientRef.current.active) {
            return;
        }

        const brokerURL = getWebSocketUrl();
        if (debug) console.log(`[WS] Connecting to ${brokerURL}`);

        const connectHeaders = {};
        if (accessToken) {
            connectHeaders['Authorization'] = `Bearer ${accessToken}`;
        }

        const client = new Client({
            brokerURL: brokerURL,
            connectHeaders,
            reconnectDelay: 5000, // Auto-reconnect every 5s
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
            webSocketFactory: () => {
                // Fallback to SockJS if native fails or strictly required, 
                // though standard Client usually tries native first if brokerURL is set.
                // Ideally we use native class for standard WS.
                return new WebSocket(brokerURL);
            },
            onConnect: (frame) => {
                if (debug) console.log('[WS] Connected');
                setIsConnected(true);
                setError(null);

                // Subscribe if topic provided
                if (topic && onMessage) {
                    client.subscribe(topic, (message) => {
                        try {
                            const parsed = JSON.parse(message.body);
                            onMessage(parsed);
                        } catch (e) {
                            console.error('[WS] Failed to parse message:', e);
                        }
                    });
                }
            },
            onDisconnect: () => {
                if (debug) console.log('[WS] Disconnected');
                setIsConnected(false);
            },
            onStompError: (frame) => {
                console.error('[WS] Broker reported error: ' + frame.headers['message']);
                console.error('[WS] Additional details: ' + frame.body);
                setError(frame.headers['message']);
            },
            onWebSocketClose: () => {
                if (debug) console.log('[WS] Connection closed');
                setIsConnected(false);
            },
        });

        client.activate();
        clientRef.current = client;
    }, [getWebSocketUrl, topic, onMessage, accessToken, debug]);

    const disconnect = useCallback(() => {
        if (clientRef.current) {
            clientRef.current.deactivate();
            clientRef.current = null;
            setIsConnected(false);
        }
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        connect();
        return () => {
            disconnect();
        };
    }, [connect, disconnect]);

    return { isConnected, error, client: clientRef.current };
};

export default useWebSocket;
